interface TelegramLeadData {
  leadSource: 'Contact Form' | 'Quick Quote';
  productName?: string;
  quantity?: number;
  country: string;
  company: string;
  contactName: string;
  email: string;
  phone: string;
  message?: string;
}

const TELEGRAM_BOT_TOKEN = process.env.NEXT_PUBLIC_TELEGRAM_BOT_TOKEN || '8399949448:AAGp9cgkDI4Leqro3RSXpNW88FVlQ1fg5nw';
const TELEGRAM_CHAT_ID = process.env.NEXT_PUBLIC_TELEGRAM_CHAT_ID || '736492045';

export const telegramService = {
  async sendLeadAlert(data: TelegramLeadData): Promise<{ success: boolean; error?: string }> {
    try {
      // Validate configuration
      if (!TELEGRAM_BOT_TOKEN || TELEGRAM_BOT_TOKEN === 'your-telegram-bot-token-here') {
        console.error('Telegram bot token not configured properly');
        return { 
          success: false, 
          error: 'Telegram bot token not configured. Please add NEXT_PUBLIC_TELEGRAM_BOT_TOKEN to your .env file.' 
        };
      }

      if (!TELEGRAM_CHAT_ID || TELEGRAM_CHAT_ID === 'your-telegram-chat-id-here') {
        console.error('Telegram chat ID not configured properly');
        return { 
          success: false, 
          error: 'Telegram chat ID not configured. Please add NEXT_PUBLIC_TELEGRAM_CHAT_ID to your .env file.' 
        };
      }

      let message = this.formatLeadMessage(data);
      
      const response = await fetch(`https://api.telegram.org/bot${TELEGRAM_BOT_TOKEN}/sendMessage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chat_id: TELEGRAM_CHAT_ID,
          text: message,
          parse_mode: 'HTML',
        }),
      });

      const responseData = await response.json();

      if (!response.ok) {
        // Provide specific error guidance for common issues
        if (responseData.description?.includes('chat not found')) {
          const setupGuide = `
❌ Telegram Chat Not Found Error

REQUIRED SETUP STEPS:
1. Open Telegram and search for your bot: @YourBotUsername
2. Click "START" or send /start to initiate conversation
3. The bot MUST receive at least one message from you before it can send notifications
4. After starting the bot, try submitting the form again

TROUBLESHOOTING:
- Verify your Chat ID (${TELEGRAM_CHAT_ID}) is correct
- Get your Chat ID by messaging @userinfobot on Telegram
- Ensure the bot token is valid and not expired
- Make sure you clicked "START" on the bot conversation

Current Configuration:
- Bot Token: ${TELEGRAM_BOT_TOKEN.substring(0, 10)}...
- Chat ID: ${TELEGRAM_CHAT_ID}
          `;
          console.error(setupGuide);
          throw new Error('Telegram bot not initialized. You must start a conversation with the bot first. See console for setup guide.');
        }
        
        throw new Error(responseData.description || 'Failed to send Telegram notification');
      }

      return { success: true };
    } catch (error: any) {
      console.error('Telegram notification error:', error.message);
      return { success: false, error: error.message };
    }
  },

  formatLeadMessage(data: TelegramLeadData): string {
    const emoji = data.leadSource === 'Contact Form' ? '📧' : '💰';
    
    let message = `${emoji} <b>New ${data.leadSource} Lead</b>\n\n`;
    
    message += `📌 <b>Lead Source:</b> ${data.leadSource}\n`;
    
    if (data.productName) {
      message += `🏍️ <b>Product:</b> ${data.productName}\n`;
    }
    
    if (data.quantity) {
      message += `📦 <b>Quantity:</b> ${data.quantity}\n`;
    }
    
    message += `🌍 <b>Country/Destination:</b> ${data.country}\n`;
    message += `🏢 <b>Company:</b> ${data.company}\n`;
    message += `👤 <b>Contact Name:</b> ${data.contactName}\n`;
    message += `📧 <b>Email:</b> ${data.email}\n`;
    message += `📞 <b>Phone:</b> ${data.phone}\n`;
    
    if (data.message) {
      message += `\n💬 <b>Message/Requirements:</b>\n${data.message}`;
    }
    
    message += `\n\n⏰ <b>Received:</b> ${new Date().toLocaleString('en-US', { 
      timeZone: 'UTC',
      dateStyle: 'medium',
      timeStyle: 'short'
    })} UTC`;
    
    return message;
  },

  // Helper method to test bot connection
  async testBotConnection(): Promise<{ success: boolean; botInfo?: any; error?: string }> {
    try {
      const response = await fetch(`https://api.telegram.org/bot${TELEGRAM_BOT_TOKEN}/getMe`);
      const data = await response.json();
      
      if (data.ok) {
        return { success: true, botInfo: data.result };
      } else {
        return { success: false, error: data.description };
      }
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }
};